import axios from 'axios';
import config from '../config';
import { decode } from './flexpolyline';

const {
  routing: { url: routingUrl },
} = config;

// HERE's routing raster tiles carry no client-accessible road-network data, so there is no
// vector layer to hit-test a click against. To resolve the road segment under a clicked point we
// issue a tiny HERE Routing API v8 request between two points a bit apart on either side of the
// click (return=polyline&spans=segmentId) and read the segment HERE snapped that route to.
// The offset is applied diagonally (both lat and lng), not along a single axis, so the synthetic
// route doesn't collapse to zero length when the clicked road happens to run east-west (a
// pure north-south offset would make both endpoints snap to the same point on such a road).
export const SEGMENT_SNAP_OFFSET_DEGREES = 0.0005;

// HERE returns the span attribute as `topologySegmentId`, not `segmentId`, prefixed with the
// traversal direction ('+' or '-') relative to the route, e.g. "+here:cm:segment:57953339".
const stripDirectionPrefix = (topologySegmentId) => topologySegmentId.replace(/^[+-]/, '');

// A short synthetic route can cross several named segments; each span marks the polyline offset
// where a new segment starts. Since the two request points straddle the actual clicked location,
// the segment active at the midpoint of the decoded polyline is the one the user clicked on.
const findSpanAtMidpoint = (spans, polylineLength) => {
  const candidates = spans.filter((span) => span.topologySegmentId);
  if (!candidates.length) return null;

  const targetOffset = Math.floor(polylineLength / 2);
  return (
    candidates.reduce(
      (best, span) =>
        span.offset <= targetOffset && (!best || span.offset > best.offset) ? span : best,
      null,
    ) || candidates[0]
  );
};

export const resolveRoadSegmentAtPoint = async ({ lat, lng }, oAuth) => {
  const origin = `${lat - SEGMENT_SNAP_OFFSET_DEGREES},${lng - SEGMENT_SNAP_OFFSET_DEGREES}`;
  const destination = `${lat + SEGMENT_SNAP_OFFSET_DEGREES},${lng + SEGMENT_SNAP_OFFSET_DEGREES}`;
  const params = {
    transportMode: 'car',
    origin,
    destination,
    // spans are computed along the route polyline, so `return` must include `polyline` or HERE
    // silently omits the spans array from the response.
    return: 'summary,polyline',
    spans: 'segmentId,names',
  };
  const query = Object.keys(params)
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');
  const headers = oAuth?.accessToken ? { Authorization: `Bearer ${oAuth.accessToken}` } : undefined;

  const { data } = await axios.get(`${routingUrl}?${query}`, { headers }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error(
      'resolveRoadSegmentAtPoint request failed',
      err.response?.status,
      err.response?.data || err.message,
    );
    throw err;
  });

  const section = data?.routes?.[0]?.sections?.[0];
  const decodedPolyline = section?.polyline ? decode(section.polyline).polyline : [];
  const span = findSpanAtMidpoint(section?.spans || [], decodedPolyline.length);

  if (!span) {
    // eslint-disable-next-line no-console
    console.error('resolveRoadSegmentAtPoint: no span with a topologySegmentId in response', data);
    throw new Error('noSegmentFound');
  }

  return {
    id: stripDirectionPrefix(span.topologySegmentId),
    name: span.names?.[0]?.value || null,
    coordinates: decodedPolyline.length ? decodedPolyline : null,
  };
};
